import { Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { Injectable } from '@angular/core';

/**
 * libs
 */
import { TranslateService } from '@ngx-translate/core';

/**
 * enums
 */
import { ILayoutLang, LayoutLang } from '@core/services/Layout/enums/layout-config';

/**
 * constants
*/
import { Langs } from './constants/langs';

/**
 * services
*/
import { LayoutService } from '@core/services/Layout/layout.service';
import { ILayoutConfig } from '@core/services/Layout/interfaces/ILayoutConfig';

/**
 * @service LangService
 * @description LangService manages the application's language configuration and internationalization.
 * This service provides a centralized way to control language settings, automatically handling
 * RTL/LTR direction changes based on the selected language. It integrates with the LayoutService
 * to ensure consistent configuration management across the application.
 */
@Injectable()
export class LangService {

  /**
   * @description Observable for language changes
   * @type {Observable<ILayoutLang>}
   * @readonly
   * @returns {Observable<ILayoutLang>} Observable for language changes
   */
  public readonly changeLang$!: Observable<ILayoutLang>

  /**
   * @description The language whose bundle is currently downloading, or `null`
   * when no switch is in flight.
   *
   * @private
   * @type {ILayoutLang | null}
   *
   * @note The layout config is no longer updated until the bundle resolves, so
   * the `currentLang == lang` guard in {@link changeLang} cannot see a switch
   * that is still loading. This field is what stops a second click on the same
   * language from queueing a duplicate load.
   */
  private _pendingLang: ILayoutLang | null = null;

  /**
   * @description Getter for the current language setting. Returns the currently active language
   * from the layout configuration. This provides read-only access to the current language state.
   * 
   * @returns {ILayoutLang} The current language setting (e.g., 'en' for English, 'ar' for Arabic)
   */
  public get lang(): ILayoutLang {
    return this._layout.layoutConfigValue.lang as ILayoutLang;
  }

  /**
   * @description Setter for the language setting. Updates the current language and automatically
   * adjusts the text direction (RTL/LTR) based on the language. The setter includes validation
   * to prevent unnecessary updates if the value is the same as the current state.
   * 
   * @param {ILayoutLang} lang - The language to set (e.g., LayoutLang.En, LayoutLang.Ar)
   * 
   * @note This setter automatically:
   * - Updates the layout configuration
   * - Sets RTL mode to true for Arabic language
   * - Sets RTL mode to false for other languages
   */
  public set lang(lang: ILayoutLang) {

    // ES6 Destruction
    const { lang: currentLang } = this._layout.layoutConfigValue;

    if (lang == null || currentLang == lang) {
      return;
    }

    const newLayoutConfig: ILayoutConfig = {
      lang,
      isRtl: lang == LayoutLang.Ar ? true : false,
    };

    // update layout config
    // save changes in localStorage
    this._layout.storeLayoutConfigChanges(newLayoutConfig);
    
  }
    
  /**
   * @description Constructor initializes the LangService and injects the LayoutService dependency.
   * The LayoutService is required for managing language configuration changes and ensuring
   * consistency with other layout settings.
   * 
   * @param {LayoutService} _layout - The LayoutService instance for configuration management
   * @param {TranslateService} _translate - The ngx-translate service backing the bundles
   *
   * @note The fallback is English rather than the stored language. Pointing it
   * at the active language made it a no-op — a key missing from `ar.json` fell
   * back to `ar.json` — so a gap in one bundle rendered as the raw key. English
   * is the complete bundle, so it is the one worth falling through to.
   *
   * @note This constructor does *not* activate a language. That is done once, at
   * bootstrap, by the `provideAppInitializer` in `app.config.ts`, so the first
   * paint already has its bundle in the store.
   */
  constructor(
    private readonly _layout: LayoutService,
    private readonly _translate: TranslateService
  ) {

    // set fallback lang
    this._translate.setFallbackLang(LayoutLang.En);

    // get change lang observable
    this.changeLang$ = this._layout.layoutConfig$.pipe(
      map((config) => config.lang),
      filter((lang) => lang != null)
    );
    
  }

  /**
   * @description Changes the application language to the specified language. This method
   * validates the language change, updates the configuration, and persists the changes
   * to localStorage. It automatically handles RTL/LTR direction changes based on the language.
   * 
   * @param {ILayoutLang} lang - The language to change to (e.g., LayoutLang.En, LayoutLang.Ar)
   *
   * @note This method:
   * - Validates that the language is different from the current setting, and
   *   that no switch to it is already in flight
   * - Loads the language bundle *before* announcing anything
   * - Updates the language configuration
   * - Automatically adjusts text direction (RTL for Arabic, LTR for others)
   * - Persists changes to localStorage
   * - Emits configuration updates to all subscribers
   *
   * @note The load has to complete first. `TranslateService.use()` is
   * asynchronous whenever the bundle is not already cached: ngx-translate only
   * sets `currentLang` and fires `onLangChange` once the file arrives. Emitting
   * {@link changeLang$} before that point left every consumer that resolves
   * strings eagerly with `TranslateService.instant()` — anything a pipe cannot
   * reach, such as an ApexCharts formatter closure — reading the outgoing
   * language, or the fallback, with no second chance to re-resolve.
   *
   * Subscribing to what `use()` returns is safe ordering-wise: it registers its
   * own internal subscriber first and the stream is `shareReplay`ed, so by the
   * time this callback runs the store is already on the new language.
   */
  public changeLang(lang: ILayoutLang): void {

    // ES6 Destruction
    const { lang: currentLang } = this._layout.layoutConfigValue;

    // if lang is null, equal the previous, or already loading then the func will skip execution
    if (lang == null || currentLang == lang || this._pendingLang == lang) {
      return;
    }

    // mark the switch as in flight
    this._pendingLang = lang;

    // update translations, then announce the change
    this._translate
        .use(lang)
        .pipe(take(1))
        .subscribe({
          next: () => {

            // set lang
            this.lang = lang;

            // update layout config
            // save changes in localStorage
            this._layout.saveLayoutConfigChanges();

            this._pendingLang = null;

          },
          error: () => {
            // the bundle failed to load — leave the app on the language it can
            // still render, and let the next attempt through
            this._pendingLang = null;
          },
        });

  }

  /**
   * @description Getter that returns an array of all available languages supported by the application.
   * This provides a centralized way to access the list of supported languages for UI components
   * like language selectors or navigation menus.
   * 
   * @returns {ILayoutLang[]} Array of supported languages (e.g., [LayoutLang.En, LayoutLang.Ar])
   */
  public get langs(): ILayoutLang[] {
    return Langs;
  }
  
}
